/**
 * Конфигурация блоков отчёта для маппинга модулей на ReportComplianceBlock
 */

import React from "react";
import { TaskStatusInfo, ReportTaskStatus } from "@/types/report";

// Типы статусов для ReportComplianceBlock
/**
 * - `not_checked` — задача ещё не дошла до исполнения (отчёт собирается);
 * - `failed` — модуль упал либо был пропущен: источник не опрошен;
 * - `not_run` — отчёт завершён, а задачи по модулю не было вовсе: не хватило
 *   данных субъекта, модуль вне запрошенных юрисдикций или исключён фильтром.
 *
 * Два последних — не «не найдено»: о субъекте они ничего не говорят.
 */
export type ComplianceCheckStatus = "found" | "not_found" | "not_checked" | "loading" | "failed" | "not_run";

/**
 * Маппинг модулей на их карточки (ленивая загрузка).
 * Используем any для props т.к. разные карточки имеют разные интерфейсы.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MODULE_CARD_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType<any>>> = {
  mvd_wanted: React.lazy(() => import("@/components/molecules/MvdWantedCard")),
  fssp_criminal_wanted: React.lazy(() => import("@/components/molecules/CriminalWantedCard")),
  fssp_debtor_wanted: React.lazy(() => import("@/components/molecules/DebtorWantedCard")),
  fsin_criminal_wanted: React.lazy(() => import("@/components/molecules/FsinCriminalWantedCard")),
  interpol_red_notice: React.lazy(() => import("@/components/molecules/InterpolRedNoticeCard")),
  interpol_yellow_notice: React.lazy(() => import("@/components/molecules/InterpolYellowNoticeCard")),
  interpol_un_notice: React.lazy(() => import("@/components/molecules/InterpolUnSanctionsCard")),
  fns_disqualified: React.lazy(() => import("@/components/molecules/DisqualifiedCard")),
  // ViolationCard использует 'violation' вместо 'record' - нужна обработка в LiveComplianceBlocks
  fns_au_violations: React.lazy(() => import("@/components/molecules/ViolationCard")),
  fssp_ip_debtor: React.lazy(() => import("@/components/molecules/ExecutionProceedingCard")),
  fssp_alimony_debtor: React.lazy(() => import("@/components/molecules/AlimonyDebtCard")),
  fns_tax_debtor: React.lazy(() => import("@/components/molecules/TaxDebtCard")),
  fns_account_suspension: React.lazy(() => import("@/components/molecules/AccountSuspensionCard")),
  efrsb_bankruptcy: React.lazy(() => import("@/components/molecules/EfrsbBankruptcyCard")),
  fnp_pledges: React.lazy(() => import("@/components/molecules/PledgeCard")),
  credit_rating: React.lazy(() => import("@/components/molecules/CreditRatingCard")),
  credit_scoring: React.lazy(() => import("@/components/molecules/CreditScoringCard")),
  fsfm_terrorists_extremists: React.lazy(() => import("@/components/molecules/TerroristExtremistCard")),
  minjust_foreign_agents: React.lazy(() => import("@/components/molecules/ForeignAgentCard")),
  government_corruptioners: React.lazy(() => import("@/components/molecules/CorruptionCard")),
  fns_mass_leaders: React.lazy(() => import("@/components/molecules/FnsMassLeadersCard")),
  fns_mass_founders: React.lazy(() => import("@/components/molecules/FnsMassFoundersCard")),
  sanctions_screening: React.lazy(() => import("@/components/molecules/WatchlistMatchCard")),
  pep_screening: React.lazy(() => import("@/components/molecules/WatchlistMatchCard")),
  cbr_warning_list_fl: React.lazy(() => import("@/components/molecules/CbrWarningListCard")),
  general_courts: React.lazy(() => import("@/components/molecules/GeneralCourtsCard")),
  arbitration_courts: React.lazy(() => import("@/components/molecules/ArbitrationCourtsCard")),
  fns_subsidiary_defendant: React.lazy(() => import("@/components/molecules/SubsidiaryDefendantCard")),
  person_business_check: React.lazy(() => import("@/components/molecules/PersonBusinessCheckCard")),
  individual_entrepreneurship: React.lazy(() => import("@/components/molecules/IndividualEntrepreneurCard")),
  self_employment_check: React.lazy(() => import("@/components/molecules/SelfEmploymentCard")),
};

/**
 * Модули, которые используют 'violation' вместо 'record' в props.
 * Сейчас пусто: `fns_au_violations` рендерится как сводная карточка
 * (special-case в LiveComplianceBlocks), а не per-record.
 */
export const VIOLATION_PROP_MODULES: string[] = [];

// Конфигурация одной проверки
export interface CheckConfig {
  module: string;
  title: string;
  statusTextFound?: string;
  statusTextNotFound?: string;
  invertColors?: boolean;
}

// Конфигурация блока источника
export interface SourceBlockConfig {
  id: string;
  sourceName: string;
  sourceLogo: string;
  checks: CheckConfig[];
}

// Конфигурация категории
export interface CategoryConfig {
  id: string;
  title: string;
  sources: SourceBlockConfig[];
}

/**
 * Полная конфигурация всех блоков отчёта
 */
export const REPORT_BLOCKS_CONFIG: CategoryConfig[] = [
  {
    id: "criminal_and_wanted",
    title: "Криминальный розыск",
    sources: [
      {
        id: "mvd",
        sourceName: "Министерство внутренних дел",
        sourceLogo: "/logos/МВД России.svg",
        checks: [
          {
            module: "mvd_wanted",
            title: "Реестр федерального розыска",
            statusTextFound: "В розыске",
            statusTextNotFound: "Не в розыске",
          },
        ],
      },
      {
        id: "fssp_criminal",
        sourceName: "Федеральная служба судебных приставов",
        sourceLogo: "/logos/ФССП России.svg",
        checks: [
          {
            module: "fssp_criminal_wanted",
            title: "Реестр розыска по подозрению в совершении преступлений",
            statusTextFound: "В розыске",
            statusTextNotFound: "Не в розыске",
          },
          {
            module: "fssp_debtor_wanted",
            title: "Реестр розыска по исполнительным производствам",
            statusTextFound: "В розыске",
            statusTextNotFound: "Не в розыске",
          },
        ],
      },
      {
        id: "fsin",
        sourceName: "Федеральная служба исполнения наказаний",
        sourceLogo: "/logos/ФСИН.svg",
        checks: [
          {
            module: "fsin_criminal_wanted",
            title: "Реестр лиц, находящихся в розыске (побег из мест лишения свободы)",
            statusTextFound: "В розыске",
            statusTextNotFound: "Не в розыске",
          },
        ],
      },
      {
        id: "interpol",
        sourceName: "Международная организация уголовной полиции (Интерпол)",
        sourceLogo: "/logos/Interpol.svg",
        checks: [
          {
            module: "interpol_red_notice",
            title: "Красные карточки (розыск для ареста)",
            statusTextFound: "В розыске",
            statusTextNotFound: "Не в розыске",
          },
          {
            module: "interpol_yellow_notice",
            title: "Жёлтые карточки (розыск пропавших)",
            statusTextFound: "В розыске",
            statusTextNotFound: "Не в розыске",
          },
          {
            module: "interpol_un_notice",
            title: "Розыск по санкциям СБ ООН",
            statusTextFound: "В списке",
            statusTextNotFound: "Не в списке",
          },
        ],
      },
    ],
  },
  {
    id: "administrative_violations",
    title: "Административные нарушения",
    sources: [
      {
        id: "fns_admin",
        sourceName: "Федеральная налоговая служба",
        sourceLogo: "/logos/ФНС России.svg",
        checks: [
          {
            module: "fns_disqualified",
            title: "Реестр дисквалифицированных лиц",
            statusTextFound: "Есть в реестре",
            statusTextNotFound: "Нет в реестре",
          },
          {
            module: "fns_au_violations",
            title: "Реестр нарушений арбитражных управляющих",
            statusTextFound: "Есть нарушения",
            statusTextNotFound: "Нет нарушений",
          },
        ],
      },
    ],
  },
  {
    id: "debts_and_pledges",
    title: "Долги и залоги",
    sources: [
      {
        id: "fssp_debts",
        sourceName: "Федеральная служба судебных приставов",
        sourceLogo: "/logos/ФССП России.svg",
        checks: [
          {
            module: "fssp_ip_debtor",
            title: "Банк данных исполнительных производств",
            statusTextFound: "Есть производства",
            statusTextNotFound: "Нет производств",
          },
          {
            module: "fssp_alimony_debtor",
            title: "Реестр должников по алиментным обязательствам",
            statusTextFound: "Есть задолженность",
            statusTextNotFound: "Нет задолженности",
          },
        ],
      },
      {
        id: "fns_debts",
        sourceName: "Федеральная налоговая служба",
        sourceLogo: "/logos/ФНС России.svg",
        checks: [
          {
            module: "fns_tax_debtor",
            title: "Реестр лиц, имеющих задолженность по уплате налогов",
            statusTextFound: "Есть задолженность",
            statusTextNotFound: "Нет задолженности",
          },
          {
            module: "fns_account_suspension",
            title: "Реестр приостановлений операций по счетам",
            statusTextFound: "Есть приостановления",
            statusTextNotFound: "Нет приостановлений",
          },
        ],
      },
      {
        id: "efrsb",
        sourceName: "ЕФРСБ",
        sourceLogo: "/logos/Федресурс.svg",
        checks: [
          {
            module: "efrsb_bankruptcy",
            title: "Единый федеральный реестр сведений о банкротстве",
            statusTextFound: "Есть в реестре",
            statusTextNotFound: "Нет в реестре",
          },
        ],
      },
      {
        id: "fnp",
        sourceName: "Федеральная нотариальная палата",
        sourceLogo: "/logos/ФНП.svg",
        checks: [
          {
            module: "fnp_pledges",
            title: "Реестр залогов движимого имущества",
            statusTextFound: "Есть залоги",
            statusTextNotFound: "Нет залогов",
          },
        ],
      },
      {
        id: "credit",
        sourceName: "Бюро кредитных историй",
        sourceLogo: "/logos/ЭБК.svg",
        checks: [
          {
            module: "credit_rating",
            title: "Кредитный рейтинг",
            statusTextFound: "Получен",
            statusTextNotFound: "Недоступен",
            invertColors: true,
          },
          {
            module: "credit_scoring",
            title: "Кредитный скоринг",
            statusTextFound: "Получен",
            statusTextNotFound: "Недоступен",
            invertColors: true,
          },
        ],
      },
    ],
  },
  {
    id: "negative_lists",
    title: "Негативные списки",
    sources: [
      {
        id: "global_watchlists",
        sourceName: "Международные списки",
        sourceLogo: "/logos/UN.svg",
        checks: [
          {
            module: "sanctions_screening",
            title: "Санкционные списки",
            statusTextFound: "Есть совпадения",
            statusTextNotFound: "Совпадений нет",
          },
          {
            module: "pep_screening",
            title: "Политически значимые лица (PEP)",
            statusTextFound: "Есть совпадения",
            statusTextNotFound: "Совпадений нет",
          },
        ],
      },
      {
        id: "rosfinmonitoring",
        sourceName: "Федеральная служба по финансовому мониторингу",
        sourceLogo: "/logos/Росфинмониторинг.svg",
        checks: [
          {
            module: "fsfm_terrorists_extremists",
            title: "Перечень лиц, причастных к терроризму и экстремизму",
            statusTextFound: "В списке",
            statusTextNotFound: "Не в списке",
          },
        ],
      },
      {
        id: "minjust",
        sourceName: "Министерство юстиции Российской Федерации",
        sourceLogo: "/logos/Министерство юстиции.svg",
        checks: [
          {
            module: "minjust_foreign_agents",
            title: "Реестр иностранных агентов",
            statusTextFound: "В реестре",
            statusTextNotFound: "Не в реестре",
          },
        ],
      },
      {
        id: "government",
        sourceName: "Единая информационная система управления кадровым составом",
        sourceLogo: "/logos/Россия.svg",
        checks: [
          {
            module: "government_corruptioners",
            title: "Реестр уволенных в связи с утратой доверия",
            statusTextFound: "В реестре",
            statusTextNotFound: "Не в реестре",
          },
        ],
      },
      {
        id: "fns_mass",
        sourceName: "Федеральная налоговая служба",
        sourceLogo: "/logos/ФНС России.svg",
        checks: [
          {
            module: "fns_mass_leaders",
            title: "Реестр массовых руководителей",
            statusTextFound: "Есть совпадения",
            statusTextNotFound: "Нет совпадений",
          },
          {
            module: "fns_mass_founders",
            title: "Реестр массовых учредителей",
            statusTextFound: "Есть совпадения",
            statusTextNotFound: "Нет совпадений",
          },
        ],
      },
      {
        id: "cbr",
        sourceName: "Банк России",
        sourceLogo: "/logos/Банк России.svg",
        checks: [
          {
            module: "cbr_warning_list_fl",
            title: "Реестр лиц, связанных с нелегальными финансовыми организациями",
            statusTextFound: "В списке",
            statusTextNotFound: "Не в списке",
          },
        ],
      },
    ],
  },
  {
    id: "courts",
    title: "Судебные дела",
    sources: [
      {
        id: "general_courts",
        sourceName: "ГАС РФ «Правосудие»",
        sourceLogo: "/logos/Верховный суд России.svg",
        checks: [
          {
            module: "general_courts",
            title: "Решения судов общей юрисдикции",
            statusTextFound: "Есть дела",
            statusTextNotFound: "Нет дел",
          },
        ],
      },
      {
        id: "arbitration",
        sourceName: "Картотека арбитражных дел",
        sourceLogo: "/logos/ВАС.svg",
        checks: [
          {
            module: "arbitration_courts",
            title: "Решения арбитражных судов",
            statusTextFound: "Есть дела",
            statusTextNotFound: "Нет дел",
          },
        ],
      },
      {
        id: "fns_subsidiary",
        sourceName: "ФНС России",
        sourceLogo: "/logos/ФНС России.svg",
        checks: [
          {
            module: "fns_subsidiary_defendant",
            title: "Реестр лиц, привлечённых к субсидиарной ответственности",
            statusTextFound: "Есть в реестре",
            statusTextNotFound: "Нет в реестре",
          },
        ],
      },
    ],
  },
  {
    id: "business_intelligence",
    title: "Бизнес-аффилированность",
    sources: [
      {
        id: "business_affiliation",
        sourceName: "Бизнес-связи и аффилированность",
        sourceLogo: "/logos/ФНС России.svg",
        checks: [
          {
            module: "individual_entrepreneurship",
            title: "Индивидуальное предпринимательство (ИП)",
            statusTextFound: "Найдены ИП",
            statusTextNotFound: "Не найдено",
          },
          {
            module: "self_employment_check",
            title: "Статус самозанятого (НПД)",
            statusTextFound: "Самозанятый",
            statusTextNotFound: "Не самозанятый",
          },
          {
            module: "person_business_check",
            title: "Связи физлица с компаниями",
            statusTextFound: "Найдены компании",
            statusTextNotFound: "Не найдено",
          },
        ],
      },
    ],
  },
];

/**
 * Маппинг статуса задачи на статус проверки.
 *
 * `reportComplete` — отчёт завершён. Пока он собирается, отсутствие задачи и
 * статус pending означают «ещё не дошли»; после завершения «Ожидание» было бы
 * неправдой: ждать уже нечего. Упавший модуль раньше тоже показывался как
 * «Ожидание» — навсегда.
 */
export function mapTaskStatusToCheckStatus(
  taskStatus: ReportTaskStatus | undefined,
  hasResults: boolean | undefined,
  reportComplete = false,
): ComplianceCheckStatus {
  if (!taskStatus) return reportComplete ? "not_run" : "not_checked";

  switch (taskStatus) {
    case "pending":
      return reportComplete ? "not_run" : "not_checked";
    case "processing":
      return "loading";
    case "completed":
      return hasResults ? "found" : "not_found";
    case "failed":
    case "skipped":
      // Находки, полученные до сбоя, важнее самого сбоя.
      return hasResults ? "found" : "failed";
    default:
      return "not_checked";
  }
}

/**
 * Получение информации о задаче по имени модуля
 */
export function getTaskForModule(
  tasks: Record<string, TaskStatusInfo>,
  moduleName: string,
): TaskStatusInfo | undefined {
  return tasks[moduleName];
}
